// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The angle maths — all pure functions, no DOM, no browser APIs. This is the
 * heart of the tool and the part unit-tested hard, because the browser
 * automation cannot tilt a real phone. Everything here takes numbers in and
 * gives numbers out: an orientation event (beta/gamma) or a gravity vector →
 * a canonical tilt; and, for the photo protractor, points → an angle.
 *
 * The canonical intermediate is a unit *gravity direction* in the device frame
 * (x = right, y = top, z = out of the screen toward the viewer). Deriving every
 * angle from that one vector keeps the platform sign-quirks in a single place
 * and makes the whole thing trivially testable with synthetic vectors.
 */

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface Point {
  x: number;
  y: number;
}

/** A tilt reading in canonical, display-ready form (all degrees). */
export interface Tilt {
  /** Screen-plane tilt from horizontal, 0..90. Sign-agnostic overall tilt. */
  fromHorizontal: number;
  /** Left-right tilt (roll), signed. Positive = right side down. */
  tiltX: number;
  /** Front-back tilt (pitch), signed. Positive = top edge down. */
  tiltY: number;
  /** Inclination of the device's long axis (top edge) from horizontal, 0..90 —
   *  the clinometer / angle-mode reading (a slope). */
  longAxisFromHorizontal: number;
}

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

export function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/**
 * Convert DeviceOrientation beta/gamma (degrees) to a unit gravity direction in
 * the device frame. Alpha (compass heading) is deliberately ignored — it does
 * not affect tilt. Flat, screen-up (beta=0, gamma=0) → (0, 0, -1): gravity
 * points straight into the back of the phone.
 */
export function betaGammaToGravity(beta: number, gamma: number): Vec3 {
  const b = beta * RAD;
  const g = gamma * RAD;
  return {
    x: Math.sin(g),
    y: -Math.sin(b) * Math.cos(g),
    z: -Math.cos(b) * Math.cos(g),
  };
}

/** Normalise a raw acceleration/gravity vector to a unit direction. Returns a
 *  flat-screen-up default if the vector is degenerate (zero length). */
export function normalizeGravity(v: Vec3): Vec3 {
  const m = Math.hypot(v.x, v.y, v.z);
  if (m < 1e-9) return { x: 0, y: 0, z: -1 };
  return { x: v.x / m, y: v.y / m, z: v.z / m };
}

/**
 * `accelerationIncludingGravity` points *away* from gravity by convention on
 * most platforms (a phone at rest reads +9.81 up the axis opposing gravity), so
 * negate it to get a "gravity points this way" direction consistent with
 * betaGammaToGravity. Sign polarity that survives this is neutralised by the
 * user's calibration zero, so the exact platform convention is not load-bearing.
 */
export function accelToGravity(ax: number, ay: number, az: number): Vec3 {
  return normalizeGravity({ x: -ax, y: -ay, z: -az });
}

/**
 * Rotate the in-plane part of a gravity vector to compensate for the UI being
 * rotated (screen.orientation.angle is 0/90/180/270). Keeps the bubble matching
 * what the user sees when they turn the phone to landscape.
 */
export function rotateGravityForScreen(g: Vec3, screenAngleDeg: number): Vec3 {
  const a = -screenAngleDeg * RAD;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return {
    x: g.x * c - g.y * s,
    y: g.x * s + g.y * c,
    z: g.z,
  };
}

/** Derive the canonical tilt from a gravity direction (need not be unit-length). */
export function tiltFromGravity(gravity: Vec3): Tilt {
  const g = normalizeGravity(gravity);
  return {
    fromHorizontal: Math.acos(clamp(Math.abs(g.z), 0, 1)) * DEG,
    // Each axis is measured against the magnitude of the other two, not against
    // -g.z alone. A shared -g.z denominator is singular when the screen is
    // vertical (g.z → 0), so a phone stood upright against a wall flipped tiltX
    // by 100°+ on a hair's movement and Calibrate stored a meaningless zero.
    // For the near-flat case this is identical to atan2(g.x, -g.z).
    tiltX: Math.atan2(g.x, Math.hypot(g.y, g.z)) * DEG,
    tiltY: Math.atan2(g.y, Math.hypot(g.x, g.z)) * DEG,
    longAxisFromHorizontal: Math.asin(clamp(Math.abs(g.y), 0, 1)) * DEG,
  };
}

/** Convenience: beta/gamma (+ optional screen angle) → canonical tilt. */
export function tiltFromOrientation(beta: number, gamma: number, screenAngleDeg = 0): Tilt {
  const g = rotateGravityForScreen(betaGammaToGravity(beta, gamma), screenAngleDeg);
  return tiltFromGravity(g);
}

/** A stored calibration zero. Subtracted from live readings so "here" reads 0°. */
export interface Calibration {
  x: number;
  y: number;
  angle: number;
}

export const NO_CALIBRATION: Calibration = { x: 0, y: 0, angle: 0 };

/** Apply a calibration zero to a tilt reading. */
export function applyCalibration(t: Tilt, cal: Calibration): Tilt {
  const tiltX = t.tiltX - cal.x;
  const tiltY = t.tiltY - cal.y;
  return {
    tiltX,
    tiltY,
    // Recompute the overall tilt from the calibrated per-axis components so the
    // headline number agrees with the bubble after zeroing.
    // A tilt from horizontal cannot exceed 90°; letting it reach 180° produced
    // negative grades and roof pitches on the far side of plumb.
    fromHorizontal: clamp(Math.hypot(tiltX, tiltY), 0, 90),
    longAxisFromHorizontal: clamp(t.longAxisFromHorizontal - cal.angle, -90, 90),
  };
}

// ── Photo protractor geometry ────────────────────────────────────────

/** Inclination of the line a→b from horizontal, 0..90 (image y grows downward). */
export function lineAngleFromHorizontal(a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return 0;
  return Math.atan2(Math.abs(dy), Math.abs(dx)) * DEG;
}

/** Interior angle at `vertex` between the rays to `a` and `b`, 0..180. */
export function angleAtVertex(a: Point, vertex: Point, b: Point): number {
  const v1x = a.x - vertex.x;
  const v1y = a.y - vertex.y;
  const v2x = b.x - vertex.x;
  const v2y = b.y - vertex.y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (m1 < 1e-9 || m2 < 1e-9) return 0;
  const cos = clamp((v1x * v2x + v1y * v2y) / (m1 * m2), -1, 1);
  return Math.acos(cos) * DEG;
}

/**
 * Index of the point closest to `target`, or null if the nearest is further away
 * than `maxDist`. Ties go to the LAST point, so the one drawn on top (and the one
 * just placed) is the one a finger grabs when points overlap.
 */
export function nearestPoint(points: Point[], target: Point, maxDist: number): number | null {
  let best: number | null = null;
  let bestDist = Infinity;
  points.forEach((p, i) => {
    const d = Math.hypot(p.x - target.x, p.y - target.y);
    if (d <= maxDist && d <= bestDist) {
      best = i;
      bestDist = d;
    }
  });
  return best;
}

/**
 * Measure an angle from a list of clicked points:
 *  - 2 points → inclination of the line from horizontal (a slope).
 *  - 3+ points → the interior angle at the middle vertex.
 * Returns null when there is nothing to measure yet.
 */
export function measurePoints(points: Point[]): { angle: number; kind: 'slope' | 'vertex' } | null {
  if (points.length === 2) {
    return { angle: lineAngleFromHorizontal(points[0], points[1]), kind: 'slope' };
  }
  if (points.length >= 3) {
    return { angle: angleAtVertex(points[0], points[1], points[2]), kind: 'vertex' };
  }
  return null;
}
